export type AnalyticsFeature =
  | "navigation"
  | "payment"
  | "camera"
  | "upload"
  | "result"
  | "error"
  | "ui";

export const KNOWN_FEATURES: ReadonlyArray<AnalyticsFeature> = [
  "navigation",
  "payment",
  "camera",
  "upload",
  "result",
  "error",
  "ui",
] as const;

export type EventParams = {
  // Generic UI
  ui_click: {
    element_id?: string;
    label?: string;
    feature?: AnalyticsFeature;
    location?: string;
    value?: number;
    // Allows auto-click to preserve a requested event name without breaking typing
    requested_event_name?: string;
  };

  // component visibility
  ui_reveal: {
    element_id?: string;
    label?: string;
    value?: number;
  }

  // Loading
  loading_start: {
    is_loading: boolean;
    timestamp: number;
    job_id?: string;
  };
  loading_end: {
    is_loading: boolean;
    job_id?: string;
    timestamp: number;
  };

  // Routing / pages
  ui_error_page_view: {
    error_type?: string;
    route_path?: string;
  };

  // Errors (app + runtime)
  app_error: {
    error_type?: string;
    message?: string;
    source?: "window_error" | "unhandledrejection" | "route_error" | "manual";
    route_path?: string;
    stack_present?: boolean;
  };

  // Payment
  payment_qr_generate_start: { child_frame_id_present: boolean };
  payment_qr_generate_success: { transaction_id_present: boolean };
  payment_qr_generate_error: { message?: string };
  payment_qr_reused: { transaction_id: string; child_frame_id: string };
  payment_qr_frame_changed: { old_frame_id: string; new_frame_id: string };
  payment_childframe_missing_in_validation: Record<string, never>;

  payment_verify_start: { transaction_id_present: boolean };
  payment_verify_success: { token_present: boolean; child_frame_id?: string };
  payment_verify_error: { message?: string };

  // Camera / capture
  camera_capture_start: { image_count: number };
  camera_photo_taken: { photo_index: number; image_count: number };
  camera_capture_complete: { photo_count: number };
  photo_capture_complete: {
    photo_count: number;
    total_attempts: number;
    had_retries: boolean;
  };

  // Upload
  upload_photos_start: { photo_count: number; child_frame_id_present: boolean };
  upload_photos_success: { photo_count: number; job_id_present: boolean };
  upload_photos_error: { message?: string };

  // Download
  download_share_opened: { file_type: "image" | "gif" };
  download_share_dismissed: { file_type: "image" | "gif" };
  download_fallback_blob: { file_type: "image" | "gif" };
  download_fallback_open: { file_type: "image" | "gif" };
  download_webview_hint: { file_type: "image" | "gif" };

  // Recovery
  recover_slip_upload_start: { slip_present: boolean };
  recover_slip_found: { job_id_present: boolean };
  recover_slip_no_photo: { child_frame_id_present: boolean };
  recover_slip_not_found: Record<string, never>;
  recover_slip_unreadable: Record<string, never>;

  // History
  history_page_view: { entry_count: number };
  history_entry_click: { entry_count: number; days_old: number };
  history_empty_view: Record<string, never>;
};

export type EventName = keyof EventParams;

export const KNOWN_EVENT_NAMES: ReadonlyArray<EventName> = [
  "ui_click",
  "ui_error_page_view",
  "app_error",
  "payment_qr_generate_start",
  "payment_qr_generate_success",
  "payment_qr_generate_error",
  "payment_qr_reused",
  "payment_qr_frame_changed",
  "payment_childframe_missing_in_validation",
  "payment_verify_start",
  "payment_verify_success",
  "payment_verify_error",
  "camera_capture_start",
  "camera_photo_taken",
  "camera_capture_complete",
  "photo_capture_complete",
  "upload_photos_start",
  "upload_photos_success",
  "upload_photos_error",
  "download_share_opened",
  "download_share_dismissed",
  "download_fallback_blob",
  "download_fallback_open",
  "download_webview_hint",
  "recover_slip_upload_start",
  "recover_slip_found",
  "recover_slip_no_photo",
  "recover_slip_not_found",
  "recover_slip_unreadable",
  "history_page_view",
  "history_entry_click",
  "history_empty_view",
] as const;
